import type { MovementStyle } from '../../types';

/**
 * MovementStylePicker - Movement style selection
 *
 * Features:
 * - Three button options: Smooth, Saccade, Linear
 * - Icons + labels
 * - Pro gating (only Smooth free)
 */

interface MovementStylePickerProps {
  value: MovementStyle;
  onChange: (style: MovementStyle) => void;
  disabled?: boolean;
  isPro: boolean;
}

const MOVEMENT_STYLES: Array<{
  value: MovementStyle;
  label: string;
  icon: string;
  description: string;
  isPro: boolean;
}> = [
  {
    value: 'smooth',
    label: 'Smooth',
    icon: '〰️',
    description: 'Smooth continuous movement side to side',
    isPro: false,
  },
  {
    value: 'spread',
    label: 'Spread',
    icon: '🖌️',
    description: 'Movement with trail/brushstroke effect',
    isPro: true,
  },
  {
    value: 'blink',
    label: 'Blink',
    icon: '⚡',
    description: 'Dot appears on one side then the other',
    isPro: true,
  },
  {
    value: 'pulse',
    label: 'Pulse',
    icon: '💫',
    description: 'Instant position changes with fading trail',
    isPro: true,
  },
];

export function MovementStylePicker({
  value,
  onChange,
  disabled = false,
  isPro,
}: MovementStylePickerProps): JSX.Element {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Movement Style
      </label>

      <div className="grid grid-cols-4 gap-2">
        {MOVEMENT_STYLES.map((style) => {
          const isLocked = style.isPro && !isPro;
          const isSelected = value === style.value;

          return (
            <button
              key={style.value}
              type="button"
              onClick={() => !isLocked && !disabled && onChange(style.value)}
              disabled={disabled || isLocked}
              className={`
                relative p-3 rounded-lg border-2 transition-all
                ${isSelected ? 'border-sync-blue bg-sync-blue/10' : 'border-gray-300 bg-white'}
                ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:border-sync-blue cursor-pointer'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={style.description}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-2xl">{style.icon}</span>
                <span className="text-xs font-medium text-gray-700">
                  {style.label}
                </span>
              </div>
              {isLocked && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
            </button>
          );
        })}
      </div>

      {!isPro && (
        <div className="text-xs text-gray-500 text-center pt-1">
          <span className="pro-icon inline-block mr-1"></span> Upgrade to Pro for Spread, Blink & Pulse
        </div>
      )}
    </div>
  );
}
